import { ComponentProps, FunctionComponent, useState } from "react";
import { Eye, Pencil, ChevronDown, ChevronUp } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "~/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";

interface AccessListSectionProps {
  icon: FunctionComponent<ComponentProps<"svg">>;
  title: string;
  items: {
    label: string;
    access: "read" | "write";
  }[];
  defaultOpen?: boolean;
}

const AccessListSection = ({
  icon: Icon,
  title,
  items,
  defaultOpen = false,
}: AccessListSectionProps) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex w-full cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 text-sm font-medium text-foreground hover:bg-muted/50 transition-colors">
        <Icon width={18} height={18} className="shrink-0 opacity-70" />
        <span className="flex-1 text-left">{title}</span>
        {open ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ul className="mt-0.5 space-y-0.5">
          {items.map((item, i) => (
            <li key={item.label} className="flex items-center gap-2 pl-6 py-0.5">
              <Tooltip delayDuration={500}>
                <TooltipTrigger asChild>
                  <span className="shrink-0 text-muted-foreground">
                    {item.access === "read" ? (
                      <Eye className="h-3.5 w-3.5" />
                    ) : (
                      <Pencil className="h-3.5 w-3.5" />
                    )}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {item.access === "read" ? "Read only" : "Read and write"}
                </TooltipContent>
              </Tooltip>
              <span className="font-mono text-sm text-foreground break-all">
                {item.label.replaceAll(`"`, "")}
              </span>
            </li>
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default AccessListSection;
